import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormBuilder, FormControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { finalize, map } from 'rxjs';
import { AlteracaoExtrato, CalendarizacaoNotificacoes } from '../../../../core/models/parametros/extrato-desligamento.model';
import { ExtratoDesligamentoService } from '../../../../core/services/parametros/extrato-desligamento.service';
import { ToastService } from '../../../../core/services/toast.service';
import {
  ANTECEDENCIAS_NOTIFICACAO_DIAS,
  CANAIS_NOTIFICACAO_OPCOES,
} from '../../../../core/utils/parametros/extrato-desligamento.defaults';
import { VivestCheckboxValueAccessorDirective } from '../../../../shared/directives/checkbox-vivest/vivest-checkbox-value-accessor.directive';
import { VivestModalAreaVisivelDirective } from '../../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { VivestSwitchValueAccessorDirective } from '../../../../shared/directives/switch-vivest/vivest-switch-value-accessor.directive';
import { LoaderService } from '../../../../shared/services/loader/loader.service';

const ROTULOS_CANAL = new Map(CANAIS_NOTIFICACAO_OPCOES.map((opcao) => [opcao.value, opcao.label]));

/** Com as notificações ativas, ao menos um prazo precisa estar marcado. */
function algumPrazoMarcado(controle: AbstractControl): ValidationErrors | null {
  return algumMarcado(controle) ? null : { nenhumPrazo: true };
}

/** Com as notificações ativas, ao menos um canal precisa estar marcado. */
function algumCanalMarcado(controle: AbstractControl): ValidationErrors | null {
  return algumMarcado(controle) ? null : { nenhumCanal: true };
}

/** Lista de checkboxes bloqueada (notificações desligadas) não é validada. */
function algumMarcado(controle: AbstractControl): boolean {
  const opcoes = controle as FormArray<FormControl<boolean>>;
  return opcoes.disabled || opcoes.controls.some((opcao) => opcao.value);
}

/**
 * Aba "Calendarização de Notificações" do extrato de desligamento: liga os lembretes automáticos
 * do prazo de adesão aos institutos, define com quantos dias de antecedência e por quais canais.
 * Desligar as notificações bloqueia prazos e canais, sem perder o que estava escolhido. As
 * mudanças só são gravadas em "Salvar" (com confirmação).
 */
@Component({
  selector: 'app-calendarizacao-notificacoes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    VivestSwitchValueAccessorDirective,
    VivestCheckboxValueAccessorDirective,
    VivestModalAreaVisivelDirective,
  ],
  // Web components do design system Vivest (card, switch, checkbox, icon, button, modal).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './calendarizacao-notificacoes.component.html',
  styleUrl: './calendarizacao-notificacoes.component.scss',
})
export class CalendarizacaoNotificacoesComponent implements OnInit {
  private readonly fb = inject(FormBuilder).nonNullable;
  private readonly extratoService = inject(ExtratoDesligamentoService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /** Id da parametrização do plano. */
  readonly planoId = input.required<number>();
  /** Nome do plano, para o resumo da configuração. */
  readonly nomePlano = input('');

  /** Gravação concluída: devolve a alteração, para a tela mostrar a última alteração. */
  readonly salvo = output<AlteracaoExtrato>();

  readonly antecedencias = ANTECEDENCIAS_NOTIFICACAO_DIAS;
  readonly canais = CANAIS_NOTIFICACAO_OPCOES;

  readonly carregando = signal(true);
  readonly salvando = signal(false);
  readonly confirmandoSalvar = signal(false);
  readonly tentouSalvar = signal(false);

  readonly form = this.fb.group({
    ativa: true,
    /** Um checkbox por prazo de `ANTECEDENCIAS_NOTIFICACAO_DIAS`, na mesma ordem. */
    prazos: this.fb.array(
      ANTECEDENCIAS_NOTIFICACAO_DIAS.map(() => this.fb.control(false)),
      { validators: [algumPrazoMarcado] },
    ),
    /** Um checkbox por canal de `CANAIS_NOTIFICACAO_OPCOES`, na mesma ordem. */
    canais: this.fb.array(
      CANAIS_NOTIFICACAO_OPCOES.map(() => this.fb.control(false)),
      { validators: [algumCanalMarcado] },
    ),
  });

  /**
   * Último estado carregado ou salvo, para o "Descartar alterações". É signal para o
   * `temAlteracoes` ser recalculado quando os dados chegam depois do primeiro desenho.
   */
  private readonly carregado = signal<CalendarizacaoNotificacoes | null>(null);

  /** Muda a cada alteração do formulário, para os `computed` abaixo serem recalculados. */
  private readonly valor = toSignal(this.form.valueChanges.pipe(map(() => this.valores())), {
    initialValue: this.valores(),
  });

  readonly ativa = computed(() => this.valor().ativa);

  readonly temAlteracoes = computed(
    () => {
      const carregado = this.carregado();
      return !!carregado && JSON.stringify(this.valor()) !== JSON.stringify(carregado) && !this.salvando();
    },
  );

  readonly erroPrazos = computed(() => {
    this.valor();
    return this.form.controls.prazos.hasError('nenhumPrazo') ? 'Marque ao menos um prazo para enviar os lembretes.' : '';
  });

  readonly erroCanais = computed(() => {
    this.valor();
    return this.form.controls.canais.hasError('nenhumCanal') ? 'Marque ao menos um canal para enviar os lembretes.' : '';
  });

  /** Resumo em uma frase do que o sistema vai fazer com a configuração da tela. */
  readonly resumo = computed(() => {
    const { ativa, antecedenciasDias, canais } = this.valor();
    const plano = this.nomePlano() ? ` do plano ${this.nomePlano()}` : '';
    if (!ativa) {
      return `As notificações automáticas estão desativadas: nenhum lembrete será enviado aos participantes${plano}.`;
    }
    if (!antecedenciasDias.length) {
      return 'Marque ao menos um prazo para os lembretes serem enviados.';
    }
    if (!canais.length) {
      return 'Marque ao menos um canal para os lembretes serem enviados.';
    }
    const rotulos = canais.map((canal) => ROTULOS_CANAL.get(canal) ?? canal);
    return (
      `O sistema enviará notificações automáticas para os participantes elegíveis${plano} ` +
      `${juntar(antecedenciasDias.map(String))} dias antes do vencimento do prazo de adesão, ` +
      `${rotulos.length > 1 ? 'pelos canais' : 'pelo canal'} ${juntar(rotulos)}.`
    );
  });

  ngOnInit(): void {
    this.form.controls.ativa.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ativa) => this.sincronizarAtiva(ativa));

    this.loaderService.showLoader();
    this.extratoService
      .obterNotificacoes(this.planoId())
      .pipe(
        finalize(() => this.carregando.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (notificacoes) => {
          this.aplicar(notificacoes);
          afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar a calendarização das notificações.', 'error');
        },
      });
  }

  // ── salvar / descartar ──────────────────────────────────────────────────────

  salvar(): void {
    this.tentouSalvar.set(true);
    if (this.temAlteracoes() && this.form.valid) {
      this.confirmandoSalvar.set(true);
    }
  }

  cancelarSalvar(): void {
    this.confirmandoSalvar.set(false);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    if (this.form.invalid || this.salvando()) {
      return;
    }
    const notificacoes = this.valores();
    this.salvando.set(true);
    this.loaderService.showLoader();
    this.extratoService
      .salvarNotificacoes(this.planoId(), notificacoes)
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ notificacoes: salvas, ultimaAlteracao }) => {
          this.aplicar(salvas);
          if (ultimaAlteracao) {
            this.salvo.emit(ultimaAlteracao);
          }
          this.toastService.show('Calendarização das notificações salva.', 'success');
        },
        error: () => this.toastService.show('Não foi possível salvar a calendarização das notificações.', 'error'),
      });
  }

  /** "Descartar alterações": volta ao último estado carregado ou salvo. */
  descartar(): void {
    const carregado = this.carregado();
    if (carregado) {
      this.aplicar(carregado);
    }
  }

  // ── formulário ──────────────────────────────────────────────────────────────

  /** O carregado é lido de volta do formulário, na ordem da tela, para comparar com as alterações. */
  private aplicar(notificacoes: CalendarizacaoNotificacoes): void {
    this.form.setValue({
      ativa: notificacoes.ativa,
      prazos: ANTECEDENCIAS_NOTIFICACAO_DIAS.map((dias) => notificacoes.antecedenciasDias.includes(dias)),
      canais: CANAIS_NOTIFICACAO_OPCOES.map((opcao) => notificacoes.canais.includes(opcao.value)),
    });
    this.carregado.set(this.valores());
    this.tentouSalvar.set(false);
    this.form.markAsPristine();
  }

  /** Notificações desligadas: prazos e canais ficam bloqueados, mas guardam o que foi escolhido. */
  private sincronizarAtiva(ativa: boolean): void {
    const { prazos, canais } = this.form.controls;
    for (const controle of [prazos, canais]) {
      if (ativa) {
        controle.enable({ emitEvent: false });
      } else {
        controle.disable({ emitEvent: false });
      }
    }
    this.form.updateValueAndValidity();
  }

  private valores(): CalendarizacaoNotificacoes {
    const { ativa, prazos, canais } = this.form.getRawValue();
    return {
      ativa,
      antecedenciasDias: ANTECEDENCIAS_NOTIFICACAO_DIAS.filter((_, i) => prazos[i]),
      canais: CANAIS_NOTIFICACAO_OPCOES.map((opcao) => opcao.value).filter((_, i) => canais[i]),
    };
  }
}

/** ["30", "15", "5"] → "30, 15 e 5"; ["E-mail", "SMS"] → "E-mail e SMS". */
function juntar(itens: string[]): string {
  return itens.length > 1 ? `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}` : (itens[0] ?? '');
}
